import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PopupContext } from 'components';
import { useContext } from 'react';
import { getPageParamFromLastPage } from 'utils/getPageParamFromLastPage';
import PRODUCTS_KEYS from '../products.keys';
import PRODUCTS_MUTATIONS from '../products.mutation';
import PRODUCTS_QUERIES from '../products.queries';

export const useFetchProductColors = ({ page, pageSize, search, ordering, filters }) =>
  useQuery({
    queryKey: PRODUCTS_KEYS.PRODUCT_COLORS_PAGINATED({
      page,
      pageSize,
      search,
      ordering,
      filters,
    }),
    queryFn: () =>
      PRODUCTS_QUERIES.FETCH_PRODUCT_COLORS({
        page,
        pageSize,
        search,
        ordering,
        filters,
      }),
    staleTime: 1 * 60 * 1000, // 1 minute
  });

export const useFetchProductColorsInfinite = ({ pageSize, search, ordering, filters }) =>
  useInfiniteQuery({
    queryKey: PRODUCTS_KEYS.PRODUCT_COLORS_INFINITE({ pageSize, filters, search, ordering }),
    queryFn: ({ pageParam = 1 }) =>
      PRODUCTS_QUERIES.FETCH_PRODUCT_COLORS({
        page: pageParam,
        pageSize,
        search,
        ordering,
        filters,
      }),
    getNextPageParam: (lastPage) => getPageParamFromLastPage(lastPage),
    staleTime: 1 * 60 * 1000, // 1 minute
  });

export const useCreateColor = () => {
  const queryClient = useQueryClient();
  const { closePopup } = useContext(PopupContext);
  return useMutation({
    mutationFn: PRODUCTS_MUTATIONS.CREATE_COLOR,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_KEYS.PRODUCT_COLORS() });
      closePopup?.();
    },
  });
};

export const useUpdateColor = () => {
  const queryClient = useQueryClient();
  const { closePopup } = useContext(PopupContext);
  return useMutation({
    mutationFn: PRODUCTS_MUTATIONS.UPDATE_COLOR,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_KEYS.PRODUCT_COLORS() });
      closePopup?.();
    },
  });
};

export const useDeleteColor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: PRODUCTS_MUTATIONS.DELETE_COLOR,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_KEYS.PRODUCT_COLORS() });
    },
  });
};

export const useGenerateColors = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: PRODUCTS_MUTATIONS.GENERATE_COLORS,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_KEYS.PRODUCT_COLORS() });
    },
  });
};
